import React, { useEffect, useRef, useState } from 'react';
import PrefixPopper from './PrefixPopper';
import usePopoverAnchor from './usePopoverAnchor';
import { PrefixData } from './model';
import CountryIcon from './CountryIcon';
import { countryPhones } from './constants';

const ChevronIcon = ({ active }: { active: boolean }) => (
    <svg
        width="16"
        height="16"
        style={{
            transform: active ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.3s',
            display: 'block',
        }}
        viewBox="0 0 24 24"
        fill="none"
        stroke="#A0A0A0"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const wrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    gap: 8,
    border: '1px solid #CBCBCD',
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
    cursor: 'pointer',
    background: '#fff',
};

type PhonePrefixSelectProps = {
    onChange: (prefix: string) => void;
    initialPhonePrefix: string;
    value?: string;
    styles?: React.CSSProperties;
};

const PhonePrefixSelect: React.FC<PhonePrefixSelectProps> = ({
    onChange,
    initialPhonePrefix,
    value,
    styles,
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    const [selectedCountry, setSelectedCountry] = useState<PrefixData | null>(() => {
        if (initialPhonePrefix) {
            return (
                countryPhones.find(({ prefix }) => {
                    return prefix === initialPhonePrefix;
                }) ?? null
            );
        }
        return null;
    });
    const popperRef = useRef<HTMLDivElement | null>(null);


    useEffect(() => {
        if (value !== selectedCountry?.prefix) {
            setSelectedCountry(() => {
                return (
                    countryPhones.find(({ prefix }) => {
                        return prefix === initialPhonePrefix;
                    }) ?? null
                );
            });
        }
    }, [value]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node) &&
                popperRef.current &&
                !popperRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    const handlePrefixChange = (prefixData: PrefixData) => {
        setSelectedCountry(prefixData);
        onChange(prefixData.prefix);
        setOpen(false);
    };

    // Позиціонування попапа (простий абсолют, або через портал, якщо хочеш)
    const [popperPos, setPopperPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const handleOpen = () => {
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            setPopperPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
        }
        setOpen(true);
    };

    return (
        <>
            <div
                ref={anchorRef}
                onClick={handleOpen}
                style={{ ...wrapperStyle, ...(styles ? styles : {}) }}
            >
                <CountryIcon flag={selectedCountry?.flag} />
                <ChevronIcon active={open} />
            </div>
            {open && (
                <div
                    ref={popperRef}
                    style={{
                        position: 'absolute',
                        zIndex: 9999,
                        top: popperPos.top,
                        left: popperPos.left,
                        boxShadow: '0px 8px 24px 0px rgba(157, 160, 164, 0.16)',
                        background: '#fff',
                        borderRadius: 6,
                    }}
                >
                    <PrefixPopper onClose={() => setOpen(false)} onChange={handlePrefixChange} />
                </div>
            )}
        </>
    );
};

export default PhonePrefixSelect;
